import { useEffect, useRef, useState } from 'react';
import IOSToggle from '@/components/IOSToggle';

/**
 * Herbruikbare hook + render-helpers voor de "Acties"-kolom header met
 * IOS-toggle (ON/OFF) en dropdown (▼) waarmee de gebruiker per rij-actie
 * de zichtbaarheid kan instellen. Voorkeur wordt opgeslagen in localStorage.
 *
 * Gebruik:
 *   const { visible, anyOn, Header, Menu } = useActiesToggle({
 *     storageKey: 'acties_vis_klanten',
 *     actions: [{ k: 'portal', label: '🔗 Portal-link' }, ...],
 *   });
 *   // in tabel-header:
 *   <th>{Header}</th>
 *   // ergens onderaan de page:
 *   {Menu}
 *   // per actie-knop:
 *   {visible.portal && <button>...</button>}
 */
export function useActiesToggle({ storageKey, actions }) {
  const keys = actions.map((a) => a.k);
  // Default-zichtbaarheid per actie. Wanneer een actie `default: false` heeft,
  // start die uit (handig voor optionele kolommen zoals Familie).
  const defaultsMap = Object.fromEntries(actions.map((a) => [a.k, a.default !== false]));
  const [visible, setVisible] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (stored && typeof stored === 'object') {
        return { ...defaultsMap, ...stored };
      }
    } catch {}
    return defaultsMap;
  });
  useEffect(() => { localStorage.setItem(storageKey, JSON.stringify(visible)); }, [visible, storageKey]);

  const anyOn = Object.values(visible).some(Boolean);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)
          && triggerRef.current && !triggerRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  const openMenu = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) setMenuPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    setMenuOpen((o) => !o);
  };
  const toggleAll = () => {
    const next = anyOn
      ? Object.fromEntries(keys.map((k) => [k, false]))
      : Object.fromEntries(keys.map((k) => [k, true]));
    setVisible(next);
  };
  const toggleOne = (k) => setVisible((p) => ({ ...p, [k]: !p[k] }));

  const Header = (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <span>Acties</span>
      <div className="relative" ref={triggerRef}>
        <div className="inline-flex items-center gap-1.5 cursor-pointer" onClick={(e) => { e.stopPropagation(); openMenu(); }} data-testid={`toggle-${storageKey}`}>
          <IOSToggle checked={anyOn} onChange={toggleAll} size="sm" />
          <span className={`inline-flex items-center justify-center w-5 h-5 border border-gray-300 hover:border-[#002FA7] hover:text-[#002FA7] text-gray-500 text-[10px] font-bold transition-colors ${menuOpen ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white'}`}>▼</span>
        </div>
      </div>
    </div>
  );

  const Menu = menuOpen ? (
    <div
      ref={menuRef}
      className="fixed bg-white border border-gray-200 shadow-2xl z-50 w-[320px]"
      style={{ top: menuPos.top, right: menuPos.right }}
      onClick={(e) => e.stopPropagation()}
      data-testid={`menu-${storageKey}`}
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50">
        <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Zichtbare acties</span>
        <button type="button" className="text-[11px] text-[#002FA7] hover:underline font-bold" onClick={toggleAll}>
          {anyOn ? 'Alles uit' : 'Alles aan'}
        </button>
      </div>
      {actions.map((a) => (
        <div key={a.k} className="flex items-center justify-between gap-3 px-4 py-2 hover:bg-gray-50 text-[13px] text-gray-700" data-testid={`opt-${storageKey}-${a.k}`}>
          <span className="flex-1 font-medium">{a.label}</span>
          <IOSToggle checked={!!visible[a.k]} onChange={() => toggleOne(a.k)} size="sm" showLabel={false} />
        </div>
      ))}
    </div>
  ) : null;

  return { visible, anyOn, Header, Menu };
}
